"use client";

import { usePurchases } from "@saas/payments/hooks/purchases";
import { getDisplayWatermarkUrl } from "@shared/lib/watermark-canvas";
import { type ImgHTMLAttributes, useEffect, useRef, useState } from "react";

type WatermarkedImageProps = Omit<
	ImgHTMLAttributes<HTMLImageElement>,
	"src"
> & { src: string };

/**
 * 生成结果图的 <img>。不能去水印的账号（没订阅、没买过积分包）看到的是前端加了水印的版本，
 * 右键另存也带水印，跟下载一致。水印加好之前不给 src，免得原图先露一下；加失败退回原图。
 * purchases 请求失败时按能去水印处理，别让付了钱的人看到水印。
 */
export function WatermarkedImage({
	src,
	alt,
	...props
}: WatermarkedImageProps) {
	const { canRemoveWatermark, loading, isError } = usePurchases();
	const needsWatermark = !loading && !isError && !canRemoveWatermark;
	const imgRef = useRef<HTMLImageElement>(null);
	const [isNearViewport, setIsNearViewport] = useState(false);
	const [watermarked, setWatermarked] = useState<{
		from: string;
		url: string;
	} | null>(null);

	// 加水印要整张原图下载再重编码，离屏的先不做（顶替调用方的 loading="lazy"）。
	// 没 src 时高度是 0，零面积的元素进了视口照样算相交
	useEffect(() => {
		const el = imgRef.current;
		if (!needsWatermark || isNearViewport || !el) {
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setIsNearViewport(true);
					observer.disconnect();
				}
			},
			{ rootMargin: "400px" },
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [needsWatermark, isNearViewport]);

	useEffect(() => {
		if (!needsWatermark || !isNearViewport) {
			return;
		}
		// 每个实例自己的 blob URL，卸载或换图时 revoke，不会误伤别处还在显示的同一张图
		const controller = new AbortController();
		let objectUrl: string | null = null;
		void getDisplayWatermarkUrl(src, controller.signal).then((url) => {
			const isObjectUrl = url.startsWith("blob:");
			if (controller.signal.aborted) {
				if (isObjectUrl) {
					URL.revokeObjectURL(url);
				}
				return;
			}
			if (isObjectUrl) {
				objectUrl = url;
			}
			setWatermarked({ from: src, url });
		});
		return () => {
			controller.abort();
			if (objectUrl) {
				URL.revokeObjectURL(objectUrl);
				// 同一张图重跑时别先拿这个已经作废的地址渲染一帧
				setWatermarked(null);
			}
		};
	}, [needsWatermark, isNearViewport, src]);

	const displaySrc = loading
		? undefined
		: needsWatermark
			? watermarked?.from === src
				? watermarked.url
				: undefined
			: src;

	// 没 src 时 alt 也先不给，不然浏览器会把整段 prompt 当替代文字画出来
	return (
		<img
			{...props}
			ref={imgRef}
			src={displaySrc}
			alt={displaySrc ? alt : ""}
		/>
	);
}
